import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "~/components/ui";
import { useAddChangeRequestReview } from "~/features/changeRequests/hooks";
import {
  type ChangeRequestReviewFormData,
  changeRequestReviewFormSchema,
  REVIEW_TYPES,
} from "~/features/changeRequests/types";

interface ChangeRequestReviewFormProps {
  changeRequestId: string;
  /** When the CR already has a non-comment review, only further comments are allowed (V1 parity). */
  hasNonCommentReviews: boolean;
}

export function ChangeRequestReviewForm({ changeRequestId, hasNonCommentReviews }: ChangeRequestReviewFormProps) {
  const { t } = useTranslation();
  const form = useForm<ChangeRequestReviewFormData>({
    resolver: zodResolver(changeRequestReviewFormSchema),
    mode: "onChange",
    defaultValues: { type: hasNonCommentReviews ? "comment" : "approve", comment: "" },
  });

  const { submit, processing } = useAddChangeRequestReview({
    changeRequestId,
    onSuccess: () => form.reset({ type: hasNonCommentReviews ? "comment" : "approve", comment: "" }),
  });

  // Once a non-comment review exists, force the type to comment.
  useEffect(() => {
    if (hasNonCommentReviews) form.setValue("type", "comment");
  }, [hasNonCommentReviews, form]);

  const availableTypes = hasNonCommentReviews ? (["comment"] as const) : REVIEW_TYPES;
  const reviewType = useWatch({ control: form.control, name: "type" });

  return (
    <Form {...form}>
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(submit)}>
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("feature.changeRequests.reviews.type")}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={hasNonCommentReviews}>
                <FormControl>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {availableTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`feature.changeRequests.reviewType.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("feature.changeRequests.reviews.comment")}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={3}
                  placeholder={
                    reviewType === "approve" ? undefined : t("feature.changeRequests.detail.reviewForm.requiredComment")
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <Button type="submit" size="sm" disabled={processing || !form.formState.isValid}>
            {t("feature.changeRequests.reviews.addReview")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
